import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { OpsDialogComponent, opsUnitAllItemName } from '../ops-dialog/ops-dialog.component';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { Vessel } from 'icreate/common/models/vessel';
import { OpsUnitService } from '@service/ops-unit.service';
import { OpsUnitSocketService } from '@service/ops-unit-socket.service';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { createDefaultOpsUnit, edgeOpsNum, OpsUnit, opsUnitmax } from 'icreate/common/models/ops-unit';
import { MessageDialogService } from '@service/message-dialog.service';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { TranslateService } from '@ngx-translate/core';
import { CATEGORY_MAX_LEN, ELLIPSIS } from 'icreate/common/models/ops-user';

@Component({
    selector: 'app-system-setting-ops',
    templateUrl: './system-setting-ops.component.html',
    styleUrls: ['./system-setting-ops.component.css'],
    standalone: false,
})
export class SystemSettingOpsComponent implements OnInit, OnDestroy {
    readonly OPS_UNIT_MAX = opsUnitmax;

    @Input() vessel!: Vessel;
    @Input() canEditSettings: boolean = true;

    @ViewChild('deleteDialog') deleteDialog: ConfirmationWindowComponent;

    deleteDialogMainLabel: string;
    categoryList: ChannelCategory[];
    opsUnitList: OpsUnit[] = [];
    selectedOpsUnit: OpsUnit;
    isOpenOption: boolean;
    isLoading: boolean = true;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private channelCategoryService: ChannelCategoryService,
        private opsUnitService: OpsUnitService,
        private opsUnitSocketService: OpsUnitSocketService,
        private messageDialogService: MessageDialogService,
        private translateService: TranslateService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        this.isOpenOption = false;
    }

    async ngOnInit() {
        this.categoryList = await this.channelCategoryService.getList(this.vessel.id);
        const opsUnits = await this.opsUnitService.getList(this.vessel.id);
        if (opsUnits && opsUnits.length > 0) {
            this.opsUnitList = opsUnits.filter(unit => unit.index <= 16).sort((a, b) => a.index - b.index);
        }

        this.opsUnitSocketService
            .opsUnitCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newOpsUnit => {
                if (newOpsUnit.vesselId === this.vessel.id) {
                    this.opsUnitList.push(newOpsUnit);
                    this.showMessageDialog('system-settings.ops.message-dialog.create.message', {
                        index: newOpsUnit.index,
                    });
                }
            });

        this.opsUnitSocketService
            .opsUnitDuplicate$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ newOpsUnit, prevOpsUnit }) => {
                if (newOpsUnit.vesselId === this.vessel.id) {
                    this.opsUnitList.push(newOpsUnit);
                    const prevOps = this.opsUnitList.find(opsUnit => opsUnit.id === prevOpsUnit.id);
                    this.showMessageDialog('system-settings.ops.message-dialog.duplicate.message', {
                        prevIndex: prevOps.index,
                        copiedIndex: newOpsUnit.index,
                    });
                }
            });

        this.opsUnitSocketService
            .opsUnitUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedOpsUnit => {
                if (updatedOpsUnit.vesselId === this.vessel.id) {
                    this.opsUnitList = this.opsUnitList.map(opsUnit => {
                        if (opsUnit.id === updatedOpsUnit.id) {
                            return updatedOpsUnit;
                        } else {
                            return opsUnit;
                        }
                    });
                    this.showMessageDialog('system-settings.ops.message-dialog.update.message', {
                        index: updatedOpsUnit.index,
                    });
                }
            });

        this.opsUnitSocketService
            .opsUnitDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ opsUnitId }) => {
                let indexToDelete = this.opsUnitList.findIndex(opsUser => opsUser.id === opsUnitId);
                if (indexToDelete !== -1) {
                    this.showMessageDialog('system-settings.ops.message-dialog.delete.message', {
                        index: this.opsUnitList[indexToDelete].index,
                    });
                    this.opsUnitList.splice(indexToDelete, 1);
                    this.opsUnitList = this.opsUnitList.map((opsUnit, index) => ({
                        ...opsUnit,
                        index: index + 1,
                    }));
                }
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    openMoreOption(opsUnit: OpsUnit) {
        if (!this.canEditSettings) return;
        if (this.isOpenOption) {
            this.isOpenOption = false;
        } else {
            this.isOpenOption = true;
            this.selectedOpsUnit = opsUnit;
        }
    }

    createMultiInputStr(opsUnit: OpsUnit & { categories: ChannelCategory[] }) {
        if (opsUnit.allCategoriesSelected) return this.allCategoriesString;
        const opsUnitCategoryList: ChannelCategory[] = opsUnit.categories;
        if (!opsUnitCategoryList || opsUnitCategoryList.length === this.categoryList.length) {
            return this.allCategoriesString;
        }

        let categoryStr = opsUnitCategoryList[0]?.category || '';
        if (opsUnitCategoryList.length !== 1) {
            categoryStr += ` (+${opsUnitCategoryList.length - 1})`;
        }

        return categoryStr.length > CATEGORY_MAX_LEN ? categoryStr.slice(0, CATEGORY_MAX_LEN) + ELLIPSIS : categoryStr;
    }

    isDuplicateDisabled(opsUnit: OpsUnit) {
        if (opsUnit.index == edgeOpsNum) return true;
        return this.opsUnitList?.length >= this.OPS_UNIT_MAX - 1;
    }

    async duplicateOpsUnit() {
        if (this.isDuplicateDisabled(this.selectedOpsUnit)) return;
        const newOpsUnit: OpsUnit & { categories: ChannelCategory[] } = structuredClone(this.selectedOpsUnit) as any;
        let selectedCategoryNameList = [];
        if (newOpsUnit.allCategoriesSelected || newOpsUnit.categories.length === this.categoryList.length) {
            selectedCategoryNameList.push(opsUnitAllItemName);
        } else {
            selectedCategoryNameList = newOpsUnit.categories.map(category => category.category);
        }

        newOpsUnit.index = this.opsUnitList.length + 1;
        delete newOpsUnit.id;

        this.dialog.open(OpsDialogComponent, {
            data: {
                categoryList: this.categoryList,
                vesselId: this.vessel.id,
                selectedCategoryNameList,
                opsUnit: newOpsUnit,
                allCategoriesSelected: newOpsUnit.allCategoriesSelected,
                isDuplicating: true,
            },
            width: '532px',
            autoFocus: false,
        });
    }

    isDeleteDisabled(opsUnit: OpsUnit) {
        if (opsUnit.index == edgeOpsNum) return true;
        else return this.opsUnitList?.length <= 1;
    }

    async deleteOpsUnit() {
        if (this.isDeleteDisabled(this.selectedOpsUnit)) return;
        try {
            this.deleteDialogMainLabel = this.translateService.instant('system-settings.ops.delete.main.title', {
                index: this.selectedOpsUnit.index,
            });

            this.deleteDialog.setVisible(true);
            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));

            if (option === ConfirmationWindowTypes.SAVE) {
                this.isLoading = true;
                await this.opsUnitService.deleteOpsUnit(this.selectedOpsUnit.id);
                this.selectedOpsUnit = null;
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    async createOpsSetting() {
        if (!this.canEditSettings) return;
        const newOpsUnit = createDefaultOpsUnit(this.opsUnitList.length + 1, this.vessel.id);

        this.dialog.open(OpsDialogComponent, {
            data: {
                categoryList: this.categoryList,
                vesselId: this.vessel.id,
                selectedCategoryNameList: [opsUnitAllItemName],
                opsUnit: newOpsUnit,
                allCategoriesSelected: true,
            },
            width: '532px',
            autoFocus: false,
        });
    }

    isEditDisabled(opsUnit: OpsUnit) {
        if (opsUnit.index == edgeOpsNum) return true;
        return false;
    }

    async editOpsSetting(opsUnit: OpsUnit & { categories: ChannelCategory[] }, index) {
        if (this.isEditDisabled(opsUnit) || !this.canEditSettings) return;
        let selectedCategoryNameList = [];
        if (opsUnit.allCategoriesSelected || opsUnit.categories.length === this.categoryList.length) {
            selectedCategoryNameList.push(opsUnitAllItemName);
        } else {
            selectedCategoryNameList = opsUnit.categories.map(category => category.category);
        }

        const currentOpsUnit = structuredClone(opsUnit);
        const dialogRef = this.dialog.open(OpsDialogComponent, {
            data: {
                categoryList: this.categoryList,
                selectedCategoryNameList,
                opsUnit: opsUnit,
                isEditing: true,
                allCategoriesSelected: opsUnit.allCategoriesSelected,
                vesselId: this.vessel.id,
            },
            width: '532px',
        });

        this.isOpenOption = false;
        this.selectedOpsUnit = null;

        const hasSavedChanges = await lastValueFrom(dialogRef.afterClosed());
        if (!hasSavedChanges) {
            this.opsUnitList.splice(index, 1, currentOpsUnit);
        }
    }

    createDefaultIp() {
        let hostPort = 0;
        if (this.opsUnitList.length) {
            const ip = this.opsUnitList[this.opsUnitList.length - 1].LineAIp;
            const parts = ip.split('.');
            hostPort = parseInt(parts[3], 10);
        }
        return {
            deafultAIp: `192.168.60.${hostPort + 1}`,
            deafultBIp: `192.168.61.${hostPort + 1}`,
        };
    }

    showMessageDialog(message: string, params?: Record<string, any>) {
        this.messageDialogService.openMessage(message, params);
    }

    get allCategoriesString() {
        return this.translateService.instant('system-settings.ops.categories.all');
    }
}
